import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import { ToastContainer } from "react-toastify";
import i18n from "./lang/i18n";
import SnackbarProvider from "./utils/contexts/SnackbarProvider";
import { UserContext } from "./utils/contexts/UserContext";
import useFindUser from "./utils/hooks/useFindUser";
import setDatepickerLocale from "./utils/setDatepickerLocale";
import ProtectedRoute from "./components/routing/ProtectedRoute/ProtectedRoute";
import RoleBasedRoute from "./components/routing/RoleBasedRoute/RoleBasedRoute";
import RouteGuard from "./components/routing/RouteGuard/RouteGuard";
import {
  TEST_RESULT_PERMISSION_CODES,
  MANAGEMENT_PERMISSION_CODES,
  MANAGEMENT_ANY,
  MANAGEMENT_USERS_PERMS,
} from "./utils/permissions";
// Static on purpose: RootGate -> Landing is the anonymous entry point and
// the LCP surface. A lazy boundary here would cost a round trip.
import RootGate from "./pages/Landing/RootGate";
import InlineSpinner from "./components/Skeleton/InlineSpinner";

/*
 * Everything below is route-level code-split. It keeps @mui/material,
 * apexcharts, chart.js, xlsx, jspdf, html2canvas and moment out of the initial
 * bundle, which the landing page at "/" would otherwise make every anonymous
 * visitor download.
 *
 * These must all stay below the static imports — CRA's `import/first` ESLint
 * rule is an error, and the prod build runs with CI=true.
 */
const BaseLayout = lazy(() => import("./components/layout/BaseLayout/BaseLayout"));

// Public legal documents. Lazy because react-markdown belongs to no other page.
const PrivacyPolicy = lazy(() => import("./pages/Legal/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/Legal/TermsOfUse"));

// Import all auth pages
const Login = lazy(() => import("./pages/Auth/Login/Login"));
const Registration = lazy(() => import("./pages/Auth/Registration/Registration"));
const PasswordReset = lazy(() => import("./pages/Auth/PasswordReset/PasswordReset"));
const PasswordResetConfirm = lazy(() => import("./pages/Auth/PasswordReset/PasswordResetConfirm"));
const Confirm = lazy(() => import("./pages/Auth/Confirm/Confirm"));

// Import all app pages
const Home = lazy(() => import("./pages/Home/Home"));
const Test = lazy(() => import("./pages/Test/Test"));
const SociometryTest = lazy(() => import("./pages/Test/TestPages/SociometryTest"));
const EPITest = lazy(() => import("./pages/Test/TestPages/EPITest"));
const BlobTreeTest = lazy(() => import("./pages/Test/TestPages/BlobTreeTest"));
const MyClassTest = lazy(() => import("./pages/Test/TestPages/MyClassTest"));
const MatolinaTest = lazy(() => import("./pages/Test/TestPages/MatolinaTest"));
const ISNTest = lazy(() => import("./pages/Test/TestPages/ISNTest"));
const BussPerryTest = lazy(() => import("./pages/Test/TestPages/BussPerryTest"));
const PhillipsTest = lazy(() => import("./pages/Test/TestPages/PhillipsTest"));
const TestResultsPage = lazy(() => import("./pages/Test/results/TestResultsPage"));
const StudentsView = lazy(() => import("./pages/Test/results/components/StudentsView"));
const ReportView = lazy(() => import("./pages/Test/results/components/ReportView"));
const OrganizationsView = lazy(() => import("./pages/Test/results/components/OrganizationsView"));
const StaffResultsPage = lazy(() => import("./pages/Test/results/StaffResultsPage"));
const StaffMembersView = lazy(() => import("./pages/Test/results/components/StaffMembersView"));
const SelfTestResultsPage = lazy(() => import("./pages/Test/results/SelfTestResultsPage"));
const Consultation = lazy(() => import("./pages/Consultation/Consultation"));
const ConsultationDetail = lazy(() => import("./pages/Consultation/ConsultationDetail"));
const Observation = lazy(() => import("./pages/Observation/Observation"));
const ObservationDetail = lazy(() => import("./pages/Observation/ObservationDetail"));
const GroupObservationDetail = lazy(() => import("./pages/Observation/GroupObservationDetail"));
const Training = lazy(() => import("./pages/Training/Training"));
const TrainingDetails = lazy(() => import("./pages/Training/TrainingDetails"));
const NewTraining = lazy(() => import("./pages/Training/NewTraining"));
const TrainingOrganizations = lazy(() => import("./pages/Training/TrainingOrganizations"));
const TrainingOrganizationTrainings = lazy(() => import("./pages/Training/TrainingOrganizationTrainings"));
const Meetings = lazy(() => import("./pages/Meetings/Meetings"));
const MeetingsOrganizations = lazy(() => import("./pages/Meetings/MeetingsOrganizations"));
const MeetingsOrganizationMeetings = lazy(() => import("./pages/Meetings/MeetingsOrganizationMeetings"));
const MeetingDetail = lazy(() => import("./pages/Meetings/MeetingDetail"));
const RiskGroup = lazy(() => import("./pages/RiskGroup/RiskGroup"));
const RiskGroupDetail = lazy(() => import("./pages/RiskGroup/RiskGroupDetail"));
const SupportGroupIndex = lazy(() => import("./pages/RiskGroup/SupportGroupIndex"));
const Management = lazy(() => import("./pages/Management/Management"));
const SchoolsManagement = lazy(() => import("./pages/Management/SchoolsManagement"));
const UsersManagement = lazy(() => import("./pages/Management/UsersManagement"));
const Help = lazy(() => import("./pages/Help/Help"));
const MySchoolClassView = lazy(() => import("./pages/MySchoolClassView/MySchoolClassView"));
const MySchoolClasses = lazy(() => import("./pages/MySchoolClasses/MySchoolClasses"));
const StudentProfile = lazy(() => import("./pages/StudentProfile/StudentProfile"));
const LoginFromBilorg = lazy(() => import("./pages/Auth/Login/LoginFromBilorg"));
const OidcCallback = lazy(() => import("./pages/Auth/Callback/OidcCallback"));
const SchoolsList = lazy(() => import("./pages/Schools/SchoolsList"));
const TestCoverage = lazy(() => import("./pages/Test/Coverage/TestCoverage"));

// Import Forms module pages
const Forms = lazy(() => import("./pages/Forms/Forms"));
const EditForm = lazy(() => import("./pages/Forms/Form/Edit/EditForm"));
const ViewForm = lazy(() => import("./pages/Forms/Form/View/ViewForm"));
const Stats = lazy(() => import("./pages/Forms/Form/Stats/Stats"));

/**
 * Shown while a lazily-loaded route chunk is in flight. Deliberately minimal —
 * it must not pull in any of the heavy page dependencies.
 */
function RouteFallback() {
  // A spinner, not a skeleton: this fires before the route is even known, so
  // there is no content shape to stand in for yet. Imported by path rather
  // than through the Skeleton barrel to keep the entry chunk small.
  return (
    <div className="flex min-h-screen items-center justify-center bg-sheet">
      <InlineSpinner className="h-10 w-10 text-primary" label="Loading" />
    </div>
  );
}

function App() {
  // getUser re-reads /auth/me. It has always existed on the hook and was never
  // reachable, so a screen that changes what /auth/me answers — editing your own
  // staff positions grants and revokes roles — had nowhere to ask for a refresh.
  // Provided rather than re-spelled at the call site: it also refreshes the
  // roles, positions and datepicker locale the hook maintains.
  const { user, setUser, getUser, isLoading } = useFindUser();

  useEffect(() => {
    setDatepickerLocale();
  }, []);

  return (
    <BrowserRouter>
      <I18nextProvider i18n={i18n}>
        <UserContext.Provider value={{ user, setUser, getUser, isLoading }}>
          <ToastContainer position="top-center" />
          <SnackbarProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public landing page at the canonical root. */}
                <Route path="/" element={<RootGate />} />

                {/* Public Auth Routes - No protection */}
                <Route path="/login" element={<Login />} />
                <Route path="/login-with-bilorg" element={<LoginFromBilorg />} />
                {/* Where bil.org.kz returns the browser after the OIDC redirect. */}
                <Route path="/auth/callback" element={<OidcCallback />} />
                <Route path="/registration" element={<Registration />} />
                <Route path="/password-reset" element={<PasswordReset />} />
                <Route
                  path="/password-reset/:token"
                  element={<PasswordResetConfirm />}
                />
                <Route path="/confirm/:token" element={<Confirm />} />

                {/*
                  Public legal documents. § 19 of the Privacy Policy and both
                  consent forms tell readers the current version is published
                  at https://qoldau.live/privacy, so this path is fixed. The
                  Terms quote /terms about themselves the same way.
                */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfUse />} />

                {/* Protected Routes - Require authentication */}
                <Route
                  element={
                    <ProtectedRoute
                      isAllowed={!!user?.uuid}
                      isLoading={isLoading}
                      redirectPath="/login"
                    />
                  }
                >
                  <Route
                    element={
                      <RoleBasedRoute
                        user={user}
                        isLoading={isLoading}
                        redirectPath="/test"
                      />
                    }
                  >
                    <Route path="/" element={<BaseLayout />}>
                      {/* Main pages with individual paths. No `index` route:
                          `/` is the public landing (see RootGate above), and
                          authenticated users are sent to `/home`. */}
                      <Route path="home" element={<Home />} />
                      <Route path="schools" element={<SchoolsList />} />
                      <Route path="school/:pk" element={<MySchoolClasses />} />
                      <Route path="classes" element={<MySchoolClasses />} />
                      <Route path="test" element={<Test />} />
                      <Route path="test-coverage" element={<TestCoverage />} />
                      <Route
                        path="test-coverage/:orgUuid"
                        element={<TestCoverage />}
                      />
                      <Route
                        path="test-coverage/:orgUuid/:groupUuid"
                        element={<TestCoverage />}
                      />
                      <Route
                        path="test/sociometry"
                        element={<SociometryTest />}
                      />
                      <Route path="test/epi" element={<EPITest />} />
                      <Route path="test/blob-tree" element={<BlobTreeTest />} />
                      <Route path="test/my-class" element={<MyClassTest />} />
                      <Route path="test/matolina" element={<MatolinaTest />} />
                      <Route path="test/isn" element={<ISNTest />} />
                      <Route path="test/generic" element={<BussPerryTest />} />
                      <Route path="test/philips" element={<PhillipsTest />} />
                      {/* New routing structure with organization layer */}
                      <Route
                        path="test/results/:testId"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              "tests.results.view_groups",
                              "tests.results.view_students",
                            ]}
                          >
                            <OrganizationsView />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/results/:testId/:orgUuid/groups"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              "tests.results.view_groups",
                              "tests.results.view_students",
                            ]}
                          >
                            <TestResultsPage />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/results/:testId/:orgUuid/groups/:groupUuid"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_students"]}
                          >
                            <StudentsView />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/results/:testId/:orgUuid/groups/:groupUuid/report"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_students"]}
                          >
                            <ReportView />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/results/:testId/:orgUuid/groups/:groupUuid/individual/:testResultUuid"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_students"]}
                          >
                            <ReportView />
                          </RouteGuard>
                        }
                      />
                      {/* Legacy routes for backward compatibility */}
                      <Route
                        path="test/results/:testId/classes/:classUuid"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_students"]}
                          >
                            <StudentsView />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/results/:testId/classes/:classUuid/report"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_students"]}
                          >
                            <ReportView />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/staff-results"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_staff"]}
                          >
                            <StaffResultsPage />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/staff-results/:testId/:orgUuid/members"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_staff"]}
                          >
                            <StaffMembersView />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/staff-results/report/:reportId"
                        element={
                          <RouteGuard
                            requiredPermissions={["tests.results.view_staff"]}
                          >
                            <ReportView />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/my-results"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              TEST_RESULT_PERMISSION_CODES.VIEW_SELF,
                            ]}
                          >
                            <SelfTestResultsPage />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="test/my-results/report/:reportId"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              TEST_RESULT_PERMISSION_CODES.VIEW_SELF,
                            ]}
                          >
                            <ReportView />
                          </RouteGuard>
                        }
                      />
                      <Route path="consultation" element={<Consultation />} />
                    
                      {/* Forms module routes. Required permissions match the
                          backend method_perms/action_perms in
                          apps/forms/views/forms.py exactly - list/retrieve
                          allow pass, manage or view_results; stats allows
                          view_results or manage. */}
                      <Route
                        path="forms"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              "forms.manage",
                              "forms.pass",
                              "forms.view_results",
                            ]}
                          >
                            <Forms />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="forms/:uuid/edit"
                        element={
                          <RouteGuard
                            requiredPermissions={["forms.manage"]}
                          >
                            <EditForm />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="forms/:uuid/view"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              "forms.manage",
                              "forms.pass",
                              "forms.view_results",
                            ]}
                          >
                            <ViewForm />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="forms/:uuid/stats"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              "forms.view_results",
                              "forms.manage",
                            ]}
                          >
                            <Stats />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="consultation/organizations"
                        element={<Consultation />}
                      />
                      <Route
                        path="consultation/organizations/:orguuid"
                        element={<Consultation />}
                      />
                      <Route path="consultation/:uuid" element={<ConsultationDetail />} />
                      <Route path="observation" element={<Observation />} />
                      <Route
                        path="observation/organizations"
                        element={<Observation />}
                      />
                      <Route
                        path="observation/organizations/:orguuid"
                        element={<Observation />}
                      />
                      <Route
                        path="observation/group/:uuid"
                        element={<GroupObservationDetail />}
                      />
                      <Route
                        path="observation/:uuid"
                        element={<ObservationDetail />}
                      />
                      <Route path="training" element={<Training />} />
                      <Route
                        path="training/organizations"
                        element={<TrainingOrganizations />}
                      />
                      <Route
                        path="training/organizations/:orguuid"
                        element={<TrainingOrganizationTrainings />}
                      />
                      <Route path="training/:id" element={<TrainingDetails />} />
                      <Route path="training/new" element={<NewTraining />} />
                      <Route path="meetings" element={<Meetings />} />
                      <Route
                        path="meetings/organizations"
                        element={<MeetingsOrganizations />}
                      />
                      <Route
                        path="meetings/organizations/:orguuid"
                        element={<MeetingsOrganizationMeetings />}
                      />
                      <Route
                        path="meetings/organizations/:orguuid/records/:uuid"
                        element={<MeetingDetail />}
                      />
                      <Route
                        path="risk-group"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              "riskzone.support_group.view_all",
                              "riskzone.support_group.view_own",
                            ]}
                          >
                            <SupportGroupIndex />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="risk-group/organizations/:orguuid"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              "riskzone.support_group.view_all",
                              "riskzone.support_group.view_own",
                            ]}
                          >
                            <RiskGroup />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="risk-group/:uuid"
                        element={<RiskGroupDetail />}
                      />
                      <Route
                        path="risk-group/:uuid/report/:reportId"
                        element={<ReportView />}
                      />
                      <Route
                        path="risk-group/student/:studentId"
                        element={<StudentProfile />}
                      />
                      <Route
                        path="risk-group/student/:studentId/report/:reportId"
                        element={<ReportView />}
                      />
                      <Route
                        path="management"
                        element={
                          <RouteGuard requiredPermissions={MANAGEMENT_ANY}>
                            <Management />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="management/schools"
                        element={
                          <RouteGuard
                            requiredPermissions={[
                              MANAGEMENT_PERMISSION_CODES.MANAGE_SCHOOLS,
                              MANAGEMENT_PERMISSION_CODES.MANAGE_ALL,
                            ]}
                          >
                            <SchoolsManagement />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="management/users"
                        element={
                          <RouteGuard requiredPermissions={MANAGEMENT_USERS_PERMS}>
                            <UsersManagement />
                          </RouteGuard>
                        }
                      />
                      <Route path="help" element={<Help />} />

                      {/* MySchool specific routes */}
                      <Route path="my-school" element={<MySchoolClasses />} />
                      <Route
                        path="my-school/classes"
                        element={<MySchoolClasses />}
                      />
                      <Route
                        path="my-school/class/:classId"
                        element={<MySchoolClassView />}
                      />
                      <Route
                        path="my-school/class/:classId/student/:studentId/profile"
                        element={<StudentProfile />}
                      />
                      <Route
                        path="my-school/class/:classId/student/:studentId/report/:reportId"
                        element={<ReportView />}
                      />

                      {/* Fallback */}
                      <Route path="*" element={<Home />} />
                    </Route>
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </SnackbarProvider>
        </UserContext.Provider>
      </I18nextProvider>
    </BrowserRouter>
  );
}

export default App;
